import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import _cloneDeep from 'lodash.clonedeep'
import { COMPOSITE_LIMITS, CompositeOutput } from '../../../../types/composite'
import { validateComposite } from '../../../../common/utils/composite'
import { usePatternContext } from '../../contexts/PatternContext'
import {
  CopilotDraftHost,
  CopilotTurnContext,
  DraftApplyResult,
  DraftEnvelope,
  PatternDraftDiff,
} from '../../copilot/types'
import { DraftStep, draftToNodes, nodesToDraft } from '../types'

const UNDO_STACK_DEPTH = 10

interface IndicatorDraftSnapshot {
  name: string
  steps: DraftStep[]
  outputs: CompositeOutput[]
}

interface UndoEntry extends IndicatorDraftSnapshot {
  toolCallId: string
}

interface PendingDraft {
  envelope: DraftEnvelope
  toolCallId: string
}

interface IndicatorHostArgs extends IndicatorDraftSnapshot {
  entityId: string | null
  setName: (name: string) => void
  setSteps: (steps: DraftStep[]) => void
  setOutputs: (outputs: CompositeOutput[]) => void
}

type NormalizedIndicatorDraft =
  | { ok: true; name?: string; steps: DraftStep[]; outputs: CompositeOutput[] }
  | { ok: false; reason: string }

// 'source' accepted structurally for legacy/agent drafts; nodesToDraft folds
// source nodes into direct source refs
const STEP_KINDS = new Set(['source', 'indicator', 'math'])

/** Structural normalization of an agent-proposed CompositeDef-shaped draft.
 * Semantic validation stays with validateComposite — invalid-but-well-formed
 * drafts apply with visible editor errors, exactly like hand edits. */
const normalizeIndicatorDraft = (raw: unknown): NormalizedIndicatorDraft => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ok: false, reason: 'Draft is not an indicator object' }
  }
  const draft: any = _cloneDeep(raw)
  if (!Array.isArray(draft.nodes) || draft.nodes.length === 0) {
    return { ok: false, reason: 'Draft has no steps' }
  }
  if (draft.nodes.length > COMPOSITE_LIMITS.maxNodes) {
    return {
      ok: false,
      reason: `Draft has ${draft.nodes.length} steps — the maximum is ${COMPOSITE_LIMITS.maxNodes}`,
    }
  }
  const seenIds = new Set<string>()
  for (const node of draft.nodes) {
    if (!node || typeof node !== 'object' || !STEP_KINDS.has(node.kind)) {
      return { ok: false, reason: 'Draft contains a malformed step' }
    }
    if (typeof node.id !== 'string' || !node.id || seenIds.has(node.id)) {
      node.id = uuidv4()
    }
    seenIds.add(node.id)
    if (node.kind === 'indicator' && (!node.params || typeof node.params !== 'object')) {
      node.params = {}
    }
  }
  const outputs: CompositeOutput[] = Array.isArray(draft.outputs)
    ? draft.outputs.filter(
        (o: any) => o && typeof o.field === 'string' && o.ref && typeof o.ref.node === 'string'
      )
    : []
  return {
    ok: true,
    name: typeof draft.name === 'string' && draft.name.trim() ? draft.name : undefined,
    steps: nodesToDraft(draft.nodes),
    outputs,
  }
}

/**
 * CopilotDraftHost over the indicator builder's draft state (exposed via
 * CompositeEditorRenderApi). Same attribution model as the pattern host:
 * agent applies record the exact steps array they set; any other commit is
 * the user's.
 */
export default function useIndicatorCopilotHost(args: IndicatorHostArgs): CopilotDraftHost {
  const { getIndicatorByValue } = usePatternContext()

  const argsRef = useRef(args)
  argsRef.current = args

  const revisionRef = useRef(0)
  const lastUserEditRevisionRef = useRef(0)
  const dirtyRef = useRef(false)
  const expectedAgentStepsRef = useRef<DraftStep[] | null>(null)
  const undoStackRef = useRef<UndoEntry[]>([])
  const appliedToolCallIdsRef = useRef<Set<string>>(new Set())
  const pendingDraftRef = useRef<PendingDraft | null>(null)

  const [pendingDraft, setPendingDraftState] = useState<PendingDraft | null>(null)
  const [validationWarnings, setValidationWarnings] = useState<string[]>([])
  const [lastAppliedToolCallId, setLastAppliedToolCallId] = useState<string | null>(null)
  const [lastRevertedToolCallId, setLastRevertedToolCallId] = useState<string | null>(null)
  // Single redo slot: the state a revert threw away, until the next apply
  const redoRef = useRef<{
    name: string
    steps: typeof argsRef.current.steps
    outputs: typeof argsRef.current.outputs
    entry: (typeof undoStackRef.current)[number]
  } | null>(null)

  const setPendingDraft = useCallback((value: PendingDraft | null) => {
    pendingDraftRef.current = value
    setPendingDraftState(value)
  }, [])

  // Attribute commits: agent applies set expectedAgentStepsRef to the exact
  // array they pass in; anything else is a user edit. The identity guard
  // (mirroring usePatternCopilotHost) keeps the initial mount — and
  // StrictMode's re-run of it — from booking a phantom user edit.
  const prevDraftRef = useRef<IndicatorDraftSnapshot | null>(null)
  useEffect(() => {
    const prev = prevDraftRef.current
    if (
      prev &&
      prev.steps === args.steps &&
      prev.outputs === args.outputs &&
      prev.name === args.name
    ) {
      return
    }
    prevDraftRef.current = { name: args.name, steps: args.steps, outputs: args.outputs }
    if (!prev) return
    if (args.steps === expectedAgentStepsRef.current) {
      expectedAgentStepsRef.current = null
      return
    }
    revisionRef.current += 1
    lastUserEditRevisionRef.current = revisionRef.current
    dirtyRef.current = true
  }, [args.steps, args.outputs, args.name])

  const getTurnContext = useCallback((): CopilotTurnContext => {
    const current = argsRef.current
    return {
      entityType: 'indicator',
      entityId: current.entityId ?? undefined,
      revision: revisionRef.current,
      dirtySinceLastAgentApply: dirtyRef.current,
      draftJson: JSON.stringify({
        name: current.name,
        nodes: draftToNodes(current.steps),
        outputs: current.outputs,
      }),
    }
  }, [])

  const performApply = useCallback(
    (envelope: DraftEnvelope, toolCallId: string): DraftApplyResult => {
      const current = argsRef.current
      const normalized = normalizeIndicatorDraft(envelope.draft)
      if (!normalized.ok) {
        return { status: 'rejected', reason: 'bad-envelope', detail: normalized.reason }
      }

      // Shallow-copied so a future in-place mutation can't corrupt entries
      undoStackRef.current = [
        ...undoStackRef.current.slice(-(UNDO_STACK_DEPTH - 1)),
        {
          name: current.name,
          steps: [...current.steps],
          outputs: [...current.outputs],
          toolCallId,
        },
      ]
      appliedToolCallIdsRef.current.add(toolCallId)
      setLastAppliedToolCallId(toolCallId)
      // A new apply supersedes whatever a previous undo could restore
      redoRef.current = null
      setLastRevertedToolCallId(null)

      expectedAgentStepsRef.current = normalized.steps
      revisionRef.current += 1
      dirtyRef.current = false
      current.setSteps(normalized.steps)
      current.setOutputs(normalized.outputs)
      if (normalized.name) current.setName(normalized.name)

      const beforeIds = new Set(current.steps.map((s) => s.id))
      const afterIds = normalized.steps.map((s) => s.id)
      const diff: PatternDraftDiff = {
        addedConditionIds: afterIds.filter((id) => !beforeIds.has(id)),
        modifiedConditionIds: [],
        removedConditionIds: [...beforeIds].filter((id) => !afterIds.includes(id)),
        addedGroupIds: [],
        removedGroupIds: [],
        summary: `${normalized.steps.length} step${normalized.steps.length === 1 ? '' : 's'}, ${normalized.outputs.length} output${normalized.outputs.length === 1 ? '' : 's'}`,
      }

      const validation = validateComposite(
        draftToNodes(normalized.steps),
        normalized.outputs,
        getIndicatorByValue
      )
      const warnings = validation.issues.filter((i) => i.level === 'error').map((i) => i.message)
      setValidationWarnings(warnings)

      return {
        status: 'applied',
        revision: revisionRef.current,
        diff,
        validationWarnings: warnings,
      }
    },
    [getIndicatorByValue]
  )

  const applyDraft = useCallback(
    (
      envelope: DraftEnvelope,
      meta: { toolCallId: string; turnStartRevision: number }
    ): DraftApplyResult => {
      if (appliedToolCallIdsRef.current.has(meta.toolCallId)) {
        return { status: 'rejected', reason: 'duplicate' }
      }
      if (envelope.entityType !== 'indicator') {
        return { status: 'rejected', reason: 'entity-mismatch', detail: envelope.entityType }
      }
      if (lastUserEditRevisionRef.current > meta.turnStartRevision) {
        setPendingDraft({ envelope, toolCallId: meta.toolCallId })
        return { status: 'held', reason: 'user-edited-mid-turn' }
      }
      return performApply(envelope, meta.toolCallId)
    },
    [performApply, setPendingDraft]
  )

  const revertLastApply = useCallback((): boolean => {
    const entry = undoStackRef.current.pop()
    if (!entry) return false
    const current = argsRef.current
    // What the revert throws away (the AI change plus later edits) is
    // exactly what redo restores
    redoRef.current = {
      name: current.name,
      steps: [...current.steps],
      outputs: [...current.outputs],
      entry,
    }
    setLastRevertedToolCallId(entry.toolCallId)
    // A revert is an explicit user rejection — book it as a user edit
    expectedAgentStepsRef.current = null
    revisionRef.current += 1
    lastUserEditRevisionRef.current = revisionRef.current
    dirtyRef.current = true
    current.setSteps(entry.steps)
    current.setOutputs(entry.outputs)
    current.setName(entry.name)
    setLastAppliedToolCallId(
      undoStackRef.current.length > 0
        ? undoStackRef.current[undoStackRef.current.length - 1].toolCallId
        : null
    )
    return true
  }, [])

  const redoLastRevert = useCallback((): boolean => {
    const redo = redoRef.current
    if (!redo) return false
    redoRef.current = null
    setLastRevertedToolCallId(null)
    const current = argsRef.current
    // Like the revert, redo is a user decision — book it as a user edit
    expectedAgentStepsRef.current = null
    revisionRef.current += 1
    lastUserEditRevisionRef.current = revisionRef.current
    dirtyRef.current = true
    current.setSteps(redo.steps)
    current.setOutputs(redo.outputs)
    current.setName(redo.name)
    // The apply is live again, so its Undo comes back
    undoStackRef.current = [...undoStackRef.current, redo.entry]
    setLastAppliedToolCallId(redo.entry.toolCallId)
    return true
  }, [])

  const resolvePendingDraft = useCallback(
    (
      action: 'apply' | 'discard'
    ): { toolCallId: string; result: DraftApplyResult | null } | null => {
      const current = pendingDraftRef.current
      setPendingDraft(null)
      if (!current) return null
      if (action === 'discard') return { toolCallId: current.toolCallId, result: null }
      return {
        toolCallId: current.toolCallId,
        result: performApply(current.envelope, current.toolCallId),
      }
    },
    [performApply, setPendingDraft]
  )

  return useMemo(
    () => ({
      entityType: 'indicator' as const,
      entityId: args.entityId,
      entityName: args.name || 'New Indicator',
      getTurnContext,
      applyDraft,
      revertLastApply,
      redoLastRevert,
      resolvePendingDraft,
      pendingDraftSummary: pendingDraft
        ? (pendingDraft.envelope.summary ?? 'AI proposed a new version of this indicator')
        : null,
      canRevert: lastAppliedToolCallId !== null,
      lastAppliedToolCallId,
      lastRevertedToolCallId,
      validationWarnings,
    }),
    [
      args.entityId,
      args.name,
      getTurnContext,
      applyDraft,
      revertLastApply,
      redoLastRevert,
      resolvePendingDraft,
      pendingDraft,
      lastAppliedToolCallId,
      lastRevertedToolCallId,
      validationWarnings,
    ]
  )
}
